import React, { useState, useEffect, useRef } from 'react';
import { IconPrinter, IconChevronDown, IconX, IconCheck } from '@tabler/icons-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../AuthContext';
import { sendNotification } from '../../lib/notify';
import Overlay from '../Overlay';
import { useTier, controlHeight } from '../../lib/tiers';
import { DESTINATIONS, getFormat } from '../../lib/labelFormats';
import {
  LABEL_PROFILES,
  optionFormatId,
  fetchBranchPrinterOptions,
  buildPrinterOptionList,
  printInventoryLabel,
  printToolLabel,
  buildInventoryZpl,
  buildToolZpl,
  dispatchZebraPrint,
  cancelPrintJob,
  subscribeToPrintJob,
} from '../../utils/printLabel';

// Plain colored-text status display — matches PrinterRegistryPage.js's
// TEST_JOB_DISPLAY from Session 48 rather than the animated progress-bar
// look from the Session 45 mockup, so the two printer-status surfaces in
// the app look and feel consistent instead of introducing a one-off style.
const JOB_STATUS_DISPLAY = {
  pending: { text: 'Job queued — waiting on relay agent', color: '#96650E' },
  sent: { text: 'Sent to printer — waiting for confirmation', color: '#1D5FA8' },
  done: { text: '✓ Printed — confirmed by printer', color: '#166C50' },
  canceled: { text: 'Canceled', color: '#9A9A9A' },
};

function PrintJobStatus({ job, onCancel, onRetry, onDismiss }) {
  if (!job) return null;
  const display = job.status === 'error'
    ? { text: `✗ ${job.error_message || "Couldn't reach the printer"}`, color: '#C62828' }
    : JOB_STATUS_DISPLAY[job.status];
  if (!display) return null;

  return (
    <div style={{ background: '#f7f7f7', border: '0.5px solid #ddd', borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ fontSize: 12.5, color: display.color, fontWeight: job.status === 'done' ? 600 : 500 }}>{display.text}</div>
      <div style={{ marginTop: 6, display: 'flex', gap: 14 }}>
        {job.status === 'pending' && (
          <span onClick={onCancel} style={{ cursor: 'pointer', color: '#A32D2D', fontSize: 11.5, fontWeight: 600 }}>Cancel job</span>
        )}
        {job.status === 'error' && (
          <span onClick={onRetry} style={{ cursor: 'pointer', color: '#1D5FA8', fontSize: 11.5, fontWeight: 600 }}>Retry</span>
        )}
        {['done', 'error', 'canceled'].includes(job.status) && (
          <span onClick={onDismiss} style={{ cursor: 'pointer', color: '#888', fontSize: 11.5, fontWeight: 600 }}>Dismiss</span>
        )}
      </div>
    </div>
  );
}

function LabelPreview({ item, branchName, profileId }) {
  // 74z8 — the preview must show what the printed label will actually carry,
  // so it reads the same tenant name printLabel.js does.
  const { companyName } = useAuth();
  const isToolFormat = profileId === 'pt_e560bt_tool';
  const isToolMode = !item?.sku;

  return (
    <div style={{ background: '#f9f9f9', border: '1px dashed #ccc', borderRadius: 8, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 180, marginBottom: 12 }}>
      <div style={{ textAlign: 'center' }}>
        {isToolFormat ? (
          // Session 75 — the 1.5"x1" tool label, drawn to the gate: a 0.85" QR
          // beside model / serial / truck. 120x80 keeps the 1.5:1 proportion.
          <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 4, width: 120, height: 80, padding: '5px 6px', display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 58, height: 58, background: '#1a1a1a', borderRadius: 2, display: 'grid', gridTemplateColumns: 'repeat(5, 9px)', gap: 1, padding: 3, boxSizing: 'border-box', flexShrink: 0 }}>
              {[1,0,1,0,1, 0,1,0,1,0, 1,0,1,0,1, 0,1,0,0,1, 1,0,0,1,0].map((px, i) => (
                <div key={i} style={{ width: 9, height: 9, background: px ? '#fff' : '#1a1a1a' }} />
              ))}
            </div>
            <div style={{ flex: 1, overflow: 'hidden', textAlign: 'right' }}>
              <div style={{ fontSize: 7, fontWeight: 700, color: '#1a1a1a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item?.name || '—'}</div>
              {item?.serial_number && (
                <div style={{ fontSize: 6, color: '#333', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>SN {item.serial_number}</div>
              )}
              {(item?.station_truck || branchName) && (
                <div style={{ fontSize: 6, color: '#555', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {[item?.station_truck, branchName].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 4, width: 160, height: 80, padding: '6px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 52, height: 52, background: '#1a1a1a', borderRadius: 2, display: 'grid', gridTemplateColumns: 'repeat(5, 8px)', gap: 1, padding: 3, boxSizing: 'border-box', flexShrink: 0 }}>
              {[1,0,1,0,1, 0,1,0,1,0, 1,0,1,0,1, 0,1,0,0,1, 1,0,0,1,0].map((px, i) => (
                <div key={i} style={{ width: 8, height: 8, background: px ? '#fff' : '#1a1a1a' }} />
              ))}
            </div>
            <div style={{ flex: 1, overflow: 'hidden', textAlign: 'left' }}>
              <div style={{ fontSize: 7, fontWeight: 700, color: '#1a1a1a', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item?.name || '—'}</div>
              {isToolMode
                ? <div style={{ fontSize: 6, color: '#555', marginBottom: 1 }}>S/N: {item?.serial_number || '—'}</div>
                : <div style={{ fontSize: 6, color: '#555', marginBottom: 1 }}>{item?.sku || '—'} · {item?.uom || '—'}</div>
              }
              <div style={{ fontSize: 5.5, color: '#777' }}>{branchName || '—'}</div>
              <div style={{ fontSize: 5, color: '#aaa', marginTop: 2 }}>{companyName}</div>
            </div>
          </div>
        )}
        <div style={{ fontSize: 10, color: '#aaa', marginTop: 8 }}>
          {isToolFormat ? '1.5 × 1 tool' : '2 × 1 standard'} · preview
        </div>
      </div>
    </div>
  );
}

export default function LabelCard({ item, branchName, isMobile, mode, variant }) {
  const { profile, labelPrintingEnabled } = useAuth();
  const isToolMode = mode === 'tool';
  // #760 - the two print buttons (sheetTrigger, iconTrigger) take their size
  // from the pointer, as the quantity stepper does: 40 to a finger, 32 to a mouse.
  const { coarse } = useTier();
  const triggerSize = coarse ? controlHeight(true, 'min') : controlHeight(false, 'commit');

  const [printerOptions, setPrinterOptions] = useState([]);
  const [loadingPrinters, setLoadingPrinters] = useState(true);
  const [activeOption, setActiveOption] = useState(null);

  const [copies, setCopies] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showProfilePicker, setShowProfilePicker] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState('');

  // Zebra dispatch state — null until a Zebra job has actually been created.
  const [printJob, setPrintJob] = useState(null);
  const jobChannelRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function loadPrinterOptions() {
      setLoadingPrinters(true);
      const { zebraPrinters } = await fetchBranchPrinterOptions(branchName);
      if (cancelled) return;

      // Session 51 — option-building extracted to printLabel.js's
      // buildPrinterOptionList so LabelCard and the new Batch Print Modal
      // can't drift into two different priority orders over time.
      //
      // #337, Session 77: hasBrother is gone from the call. The list now always
      // carries the local destination, so the "No printer set up at this branch
      // yet" empty state below has become unreachable in practice. Left in place
      // deliberately — it is the honest thing to show if buildPrinterOptionList
      // ever returns nothing, and removing it would be assuming it cannot.
      const options = buildPrinterOptionList({ zebraPrinters, mode });

      setPrinterOptions(options);
      setActiveOption(options[0] || null);
      setLoadingPrinters(false);
    }

    loadPrinterOptions();
    return () => { cancelled = true; };
  }, [branchName, isToolMode]);

  // Clean up any open job subscription if this card unmounts entirely
  // (e.g. navigating away) while a job is still pending.
  useEffect(() => {
    return () => {
      if (jobChannelRef.current) supabase.removeChannel(jobChannelRef.current);
    };
  }, []);

  // #199 part 3, Session 79.22b.6.1f (approved drawing C) -- the company has
  // turned QR label printing off, so this card is not offered at all: not
  // greyed, because a Print button that can never print is a promise Stede
  // cannot keep (C2). One check here covers the item page, the tool page and
  // the Fleet icons, every place that renders this card. AFTER every hook
  // above, deliberately -- an early return before a hook is the rules-of-hooks
  // error that #417's foundation once shipped past 1,892 green tests.
  // `=== false`: only an explicit OFF hides it; a reader that does not know the
  // switch (a stand-in sign-in, an older provider) keeps today's behaviour.
  if (labelPrintingEnabled === false) return null;

  // #337, Session 77. The picker used to read a LABEL_PROFILES row and render
  // "PT-E560BT · 2\"×1\" standard", naming a machine Stede cannot identify — the
  // same defect Session 76 fixed in the batch modal, surviving here because that
  // session did not open this file.
  //
  // Deliberately NOT fixed by copying the modal's single destination row: this
  // is a choice AMONG options, so it needs the size to tell two local rows apart
  // in tool mode. Destination label from DESTINATIONS, size from the ladder
  // (#334) — never from the profile's hand-written `desc`.
  function optionLabel(opt) {
    if (!opt) return '';
    if (opt.type === 'zebra') return `Zebra — ${opt.printer.name}`;
    const format = getFormat(opt.formatId || optionFormatId(opt));
    return format ? `${DESTINATIONS.local.label} · ${format.label}` : DESTINATIONS.local.label;
  }

  // The verb comes from DESTINATIONS too. "Prints instantly from this computer"
  // was the same promise the modal's old "Prints now" made and could not keep.
  function optionSub(opt) {
    if (!opt) return '';
    return opt.type === 'zebra'
      ? `${branchName} · queued over network`
      : DESTINATIONS.local.verb;
  }

  function resetPrintJob() {
    if (jobChannelRef.current) {
      supabase.removeChannel(jobChannelRef.current);
      jobChannelRef.current = null;
    }
    setPrintJob(null);
  }

  function closeModalAndReset() {
    setShowModal(false);
    setPrintError('');
    resetPrintJob();
  }

  // Fires once a Zebra job reaches a final state. tool_history is only
  // logged here (on confirmed 'done'), not at dispatch — unlike Brother,
  // Zebra actually confirms the print happened, so logging at dispatch
  // time the way Brother does would risk a history entry for a print that
  // never actually completed.
  //
  // Known limitation, not fixed tonight: this only fires if this browser
  // tab is still open when the job resolves, since the notification is
  // written client-side in reaction to the Realtime update. A durable
  // version (a DB trigger writing the notification directly) is worth
  // doing once 8d's relay agent is real and jobs can resolve on their own
  // time — doesn't matter yet, since nothing resolves at all without a
  // relay agent to begin with.
  async function handleZebraJobResolved(updatedJob) {
    const domain = isToolMode ? 'tools' : 'inventory';
    const linkTo = isToolMode ? 'tool_detail' : 'inv_detail';

    if (updatedJob.status === 'done') {
      if (isToolMode) {
        await supabase.from('tool_history').insert({
          tool_id: item.id,
          event_type: 'label_printed',
          changed_by_name: profile?.full_name || null,
          changed_by_id: profile?.id || null,
          notes: `${copies} label${copies !== 1 ? 's' : ''} printed · ${optionLabel(activeOption)}`,
        });
      }
      await sendNotification(
        [profile.id],
        'Label printed',
        `${item?.name || 'Item'} printed via ${optionLabel(activeOption)}.`,
        'print_job_done',
        domain,
        linkTo,
        item.id
      );
    } else if (updatedJob.status === 'error') {
      await sendNotification(
        [profile.id],
        "Couldn't print label",
        `${item?.name || 'Item'} failed to print via ${optionLabel(activeOption)}: ${updatedJob.error_message || 'no response from printer'}.`,
        'print_job_error',
        domain,
        linkTo,
        item.id
      );
    }
  }

  async function handleCancelJob() {
    if (!printJob) return;
    // Doesn't optimistically mark this canceled — the guarded UPDATE in
    // cancelPrintJob only applies while status is still 'pending', so if
    // the relay had already picked it up a moment earlier, this quietly
    // does nothing and the Realtime subscription reflects whatever
    // actually happens next (sent/done/error) instead of a status we
    // merely assumed. Same reasoning as Session 48's DELETE-verification
    // principle, applied here to a cancel instead of a cleanup.
    await cancelPrintJob(printJob.id);
  }

  async function handlePrint() {
    if (!activeOption) return;

    if (activeOption.type === 'local') {
      setPrinting(true);
      setPrintError('');

      try {
        let result;
        if (isToolMode) {
          result = await printToolLabel(item, branchName, copies, activeOption.profileId);
        } else {
          result = await printInventoryLabel(item, branchName, copies);
        }

        if (!result.success) {
          // #339, Session 77. The pop-up branch is gone, because the mechanism
          // is. A hidden same-document iframe needs no permission, so there is
          // no longer a failure a user can fix in their browser settings.
          setPrintError(result.error === 'print_frame_failed'
            ? 'Could not open the print view. Reload the page and try again.'
            : 'Print failed. Please try again.');
          setPrinting(false);
          return;
        }

        if (isToolMode) {
          await supabase.from('tool_history').insert({
            tool_id: item.id,
            event_type: 'label_printed',
            changed_by_name: profile?.full_name || null,
            changed_by_id: profile?.id || null,
            notes: `${copies} label${copies !== 1 ? 's' : ''} printed · ${optionLabel(activeOption)}`,
          });
        }

        // #335, Session 79.22b.6.3.2 (Colby's R1 D1): the "reprint the last job"
        // store is gone. It saved a function through JSON, which cannot hold one,
        // and nothing ever read it back. Reprinting, if it is ever wanted, comes
        // from #326's export history.
        setShowModal(false);
      } catch {
        setPrintError('Unexpected error. Please try again.');
      } finally {
        setPrinting(false);
      }
      return;
    }

    // Zebra path — creates a real print_jobs row and watches it live
    // instead of assuming success on click. Brother above is completely
    // unaffected by anything below this point.
    setPrintError('');
    const profileForZpl = LABEL_PROFILES[activeOption.profileId];
    const zpl = isToolMode
      ? buildToolZpl(item, branchName, profileForZpl)
      : buildInventoryZpl(item, branchName, profileForZpl);

    const dispatch = await dispatchZebraPrint({
      printer: activeOption.printer,
      userId: profile?.id,
      itemType: isToolMode ? 'tool' : 'inventory',
      itemId: item.id,
      copies,
      zplPayload: zpl,
    });

    if (!dispatch.success) {
      setPrintError('Could not create the print job: ' + dispatch.error);
      return;
    }

    setPrintJob({ id: dispatch.job.id, status: 'pending', error_message: null });
    jobChannelRef.current = subscribeToPrintJob(dispatch.job.id, (updated) => {
      setPrintJob({ id: updated.id, status: updated.status, error_message: updated.error_message });
      if (updated.status === 'done' || updated.status === 'error') {
        handleZebraJobResolved(updated);
      }
    });
  }

  const printDisabled = printing || !activeOption;

  if (isMobile) {
    const innerContent = (
      <>
        {loadingPrinters ? (
          <div style={{ fontSize: 12, color: '#aaa', padding: '9px 12px', marginBottom: 11 }}>Checking available printers…</div>
        ) : printerOptions.length === 0 ? (
          <div style={{ background: '#FFF8E8', color: '#96650E', borderRadius: 8, padding: '10px 12px', fontSize: 12, marginBottom: 11 }}>
            No printer set up at this branch yet.
          </div>
        ) : (
          <>
            <div
              onClick={() => setShowProfilePicker(p => !p)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f7f7f7', border: '0.5px solid #ddd', borderRadius: 8, padding: '9px 12px', marginBottom: 11, cursor: 'pointer' }}
            >
              <span style={{ fontSize: 13, color: '#444' }}>Printing to: <strong style={{ color: '#1a1a1a' }}>{optionLabel(activeOption)}</strong></span>
              <IconChevronDown size={14} color="#888" />
            </div>
            {showProfilePicker && (
              <div style={{ background: '#fff', border: '0.5px solid #ddd', borderRadius: 8, marginBottom: 11, overflow: 'hidden' }}>
                {printerOptions.map(opt => (
                  <div
                    key={opt.key}
                    onClick={() => { setActiveOption(opt); setShowProfilePicker(false); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '0.5px solid #f0f0f0', background: activeOption?.key === opt.key ? '#e8f8f2' : '#fff', cursor: 'pointer' }}
                  >
                    <IconPrinter size={14} color={activeOption?.key === opt.key ? '#1D9E75' : '#aaa'} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a1a' }}>{optionLabel(opt)}</div>
                      <div style={{ fontSize: 11, color: '#888' }}>{optionSub(opt)}</div>
                    </div>
                    {activeOption?.key === opt.key && <IconCheck size={13} color="#1D9E75" />}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {printError && (
          <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 10 }}>{printError}</div>
        )}

        {printJob ? (
          <PrintJobStatus job={printJob} onCancel={handleCancelJob} onRetry={handlePrint} onDismiss={resetPrintJob} />
        ) : printerOptions.length > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
            <div
              onClick={() => { const n = parseInt(prompt('Number of copies:', copies)) || 1; setCopies(Math.min(99, Math.max(1, n))); }}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f7f7f7', border: '0.5px solid #ddd', borderRadius: 8, padding: '8px 13px', minWidth: 56, cursor: 'pointer' }}
            >
              <span style={{ fontSize: 9, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>Copies</span>
              <span style={{ fontSize: 20, fontWeight: 500, color: '#1a1a1a', lineHeight: 1 }}>{copies}</span>
            </div>
            <button
              onClick={handlePrint}
              disabled={printDisabled}
              style={{ background: printDisabled ? '#aaa' : '#1D9E75', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: printDisabled ? 'not-allowed' : 'pointer', padding: '0 14px', minHeight: 44 }}
            >
              <IconPrinter size={17} />
              {printing ? 'Printing...' : `Print ${copies === 1 ? 'label' : `${copies} labels`}`}
            </button>
          </div>
        )}
        <div style={{ fontSize: 11, color: '#aaa', textAlign: 'center', marginTop: 8 }}>Tap copies to change · always defaults to 1</div>
      </>
    );

    // Session 51 — sheetTrigger: a compact icon button (FleetPage mobile
    // card) that opens the same picker/copies/print UI in a bottom sheet
    // instead of always rendering inline. Reuses showModal/closeModalAndReset
    // — same state the desktop modal already uses — since a LabelCard
    // instance only ever renders one variant at a time.
    if (variant === 'sheetTrigger') {
      return (
        <>
          {/* #760 (approved drawing, section 5) - sized by the pointer, and
              NEVER switched off. It was switched off while the printers loaded
              and whenever the list came back empty - a press answered with
              nothing (#492). Since #337 the list always holds this device's own
              printer, so empty is unreachable in practice; if it ever is, the
              sheet says "No printer set up at this branch yet." */}
          <button
            onClick={() => { setPrintError(''); setShowModal(true); }}
            aria-label="Print label"
            title="Print label"
            style={{
              width: triggerSize, height: triggerSize, borderRadius: 8, flexShrink: 0,
              border: '1px solid #B5D4F4', background: '#fff', color: '#185FA5',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }}
          >
            <IconPrinter size={18} />
          </button>
          {showModal && (
            <Overlay variant="sheet" onClose={closeModalAndReset}>
              <div style={{ padding: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
                  <div style={{ width: 36, height: 4, borderRadius: 2, background: '#ddd' }} />
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a', marginBottom: 2 }}>Label — {item?.name}</div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 14 }}>
                  {isToolMode ? `S/N: ${item?.serial_number || '—'} · ${branchName}` : `SKU: ${item?.sku} · ${branchName}`}
                </div>
                {innerContent}
              </div>
            </Overlay>
          )}
        </>
      );
    }

    return (
      <div style={{ background: '#fff', borderRadius: 12, border: '0.5px solid #e0e0e0', padding: '13px 15px', marginBottom: 12 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Label</div>
        {innerContent}
      </div>
    );
  }

  return (
    <>
      {variant === 'iconTrigger' ? (
        // #760 - as the sheetTrigger above: sized by the pointer, never switched
        // off (not even while the printers load); the dialog answers the press.
        <button
          onClick={() => { setPrintError(''); setShowModal(true); }}
          aria-label="Print label"
          title="Print label"
          style={{
            width: triggerSize, height: triggerSize, borderRadius: 6, flexShrink: 0,
            border: '1px solid #B5D4F4', background: '#fff', color: '#185FA5',
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          }}
        >
          <IconPrinter size={16} />
        </button>
      ) : (
      <div style={{ border: '0.5px solid #9FE1CB', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
        <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.08em', color: '#0F6E56', textTransform: 'uppercase', padding: '9px 12px 7px', borderBottom: '0.5px solid #9FE1CB', background: '#e8f8f2', display: 'flex', alignItems: 'center', gap: 6 }}>
          <IconPrinter size={13} color="#1D9E75" />
          Label
        </div>
        <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {loadingPrinters ? (
            <div style={{ fontSize: 12, color: '#aaa' }}>Checking available printers…</div>
          ) : printerOptions.length === 0 ? (
            <div style={{ background: '#FFF8E8', color: '#96650E', borderRadius: 6, padding: '8px 10px', fontSize: 12 }}>
              No printer set up at this branch yet.
            </div>
          ) : (
            <>
              <div
                onClick={() => setShowProfilePicker(p => !p)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f7f7f7', border: '0.5px solid #ddd', borderRadius: 6, padding: '7px 10px', cursor: 'pointer' }}
              >
                <div style={{ fontSize: 12, color: '#1a1a1a' }}>{optionLabel(activeOption)}</div>
                <IconChevronDown size={13} color="#888" />
              </div>
              {showProfilePicker && (
                <div style={{ background: '#fff', border: '0.5px solid #ddd', borderRadius: 6, overflow: 'hidden', marginTop: -4 }}>
                  {printerOptions.map(opt => (
                    <div
                      key={opt.key}
                      onClick={() => { setActiveOption(opt); setShowProfilePicker(false); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderBottom: '0.5px solid #f0f0f0', background: activeOption?.key === opt.key ? '#e8f8f2' : '#fff', cursor: 'pointer' }}
                    >
                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#1D9E75', flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 500, color: '#1a1a1a', margin: 0 }}>{optionLabel(opt)}</div>
                        <div style={{ fontSize: 10, color: '#888', margin: 0 }}>{optionSub(opt)}</div>
                      </div>
                      {activeOption?.key === opt.key && <IconCheck size={13} color="#1D9E75" />}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {printError && (
            <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 6, padding: '6px 10px', fontSize: 11 }}>{printError}</div>
          )}

          {printerOptions.length > 0 && (
            <div style={{ display: 'flex', gap: 7, alignItems: 'stretch' }}>
              <div
                onClick={() => { const n = parseInt(prompt('Number of copies:', copies)) || 1; setCopies(Math.min(99, Math.max(1, n))); }}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f7f7f7', border: '0.5px solid #ddd', borderRadius: 6, padding: '6px 10px', minWidth: 50, cursor: 'pointer' }}
              >
                <span style={{ fontSize: 9, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 1 }}>Copies</span>
                <span style={{ fontSize: 18, fontWeight: 500, color: '#1a1a1a', lineHeight: 1 }}>{copies}</span>
              </div>
              <button
                onClick={() => { setPrintError(''); setShowModal(true); }}
                disabled={!activeOption}
                style={{ background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 500, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, cursor: 'pointer', padding: '0 10px', minHeight: 40 }}
              >
                <IconPrinter size={14} />
                Print label
              </button>
            </div>
          )}
          <div style={{ fontSize: 10, color: '#aaa', textAlign: 'center' }}>Opens preview · confirm before printing</div>
        </div>
      </div>
      )}

      {showModal && (
        <Overlay variant="modal" onClose={closeModalAndReset}>
            <div style={{ padding: '16px 20px 13px', borderBottom: '0.5px solid #e5e5e5', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 500, color: '#1a1a1a', marginBottom: 2 }}>Print label — {item?.name}</div>
                <div style={{ fontSize: 11, color: '#888' }}>
                  {isToolMode
                    ? `S/N: ${item?.serial_number || '—'} · ${branchName}`
                    : `SKU: ${item?.sku} · ${item?.uom} · ${branchName}`
                  }
                </div>
              </div>
              <button onClick={closeModalAndReset} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', padding: 0 }}>
                <IconX size={18} />
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '210px 1fr', minHeight: 320 }}>
              <div style={{ borderRight: '0.5px solid #e5e5e5', padding: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.08em', color: '#888', textTransform: 'uppercase', marginBottom: 10 }}>Label size & printer</div>
                {printerOptions.map(opt => (
                  <div
                    key={opt.key}
                    onClick={() => setActiveOption(opt)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 9px', borderRadius: 7, marginBottom: 3, background: activeOption?.key === opt.key ? '#e8f8f2' : 'transparent', cursor: 'pointer' }}
                  >
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#1D9E75', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: '#1a1a1a', margin: 0 }}>{optionLabel(opt)}</div>
                      <div style={{ fontSize: 10, color: '#888', margin: 0 }}>{optionSub(opt)}</div>
                    </div>
                    {activeOption?.key === opt.key && <IconCheck size={13} color="#1D9E75" />}
                  </div>
                ))}
                <div style={{ height: '0.5px', background: '#e5e5e5', margin: '14px 0' }} />
                <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.08em', color: '#888', textTransform: 'uppercase', marginBottom: 8 }}>Copies</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <input
                    type="number"
                    min={1}
                    max={99}
                    value={copies}
                    onChange={e => setCopies(Math.min(99, Math.max(1, parseInt(e.target.value) || 1)))}
                    onFocus={e => e.target.select()}
                    style={{ width: 52, background: '#f7f7f7', border: '0.5px solid #ddd', borderRadius: 7, padding: '6px 8px', fontWeight: 500, color: '#1a1a1a', textAlign: 'center' }}
                  />
                  <span style={{ fontSize: 11, color: '#aaa' }}>labels</span>
                </div>
                {activeOption && (
                  <div style={{ fontSize: 10, background: '#e8f8f2', color: '#0F6E56', borderRadius: 4, padding: '2px 7px', display: 'inline-block' }}>
                    {/* #334: inches from the ladder, which is what the batch
                        modal's chips show and what the label is specified in.
                        The millimetres this used to print came from
                        LABEL_PROFILES and were a rendering detail. */}
                    {getFormat(activeOption.formatId || optionFormatId(activeOption))?.label} in physical
                  </div>
                )}
              </div>
              <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.08em', color: '#888', textTransform: 'uppercase', marginBottom: 12 }}>Label preview</div>
                <LabelPreview item={item} branchName={branchName} profileId={activeOption?.profileId} />
                <div style={{ fontSize: 10, color: '#aaa', textAlign: 'center' }}>Profile controls physical size · preview updates on selection</div>
              </div>
            </div>
            {printError && (
              <div style={{ margin: '0 20px 12px', background: '#FFEBEE', color: '#A32D2D', borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>{printError}</div>
            )}
            {printJob ? (
              <div style={{ padding: '0 20px 12px' }}>
                <PrintJobStatus job={printJob} onCancel={handleCancelJob} onRetry={handlePrint} onDismiss={resetPrintJob} />
              </div>
            ) : null}
            {printJob ? (
              <div style={{ padding: '12px 20px', borderTop: '0.5px solid #e5e5e5', display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={closeModalAndReset} style={{ background: 'none', border: '0.5px solid #ccc', borderRadius: 7, padding: '8px 16px', fontSize: 13, color: '#555', cursor: 'pointer' }}>Close</button>
              </div>
            ) : (
              <div style={{ padding: '12px 20px', borderTop: '0.5px solid #e5e5e5', display: 'flex', alignItems: 'center', gap: 9, justifyContent: 'flex-end' }}>
                <button onClick={closeModalAndReset} style={{ background: 'none', border: '0.5px solid #ccc', borderRadius: 7, padding: '8px 16px', fontSize: 13, color: '#555', cursor: 'pointer' }}>Cancel</button>
                <button
                  onClick={handlePrint}
                  disabled={printDisabled}
                  style={{ background: printDisabled ? '#aaa' : '#1D9E75', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 20px', fontSize: 13, fontWeight: 500, cursor: printDisabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <IconPrinter size={15} />
                  {printing ? 'Printing...' : `Print ${copies} label${copies !== 1 ? 's' : ''}`}
                </button>
              </div>
            )}
          </Overlay>
      )}
    </>
  );
}